import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Scenario: Hiro, 38, M — Gout & Uric Acid (Metabolic Health series).

export const goutHiro: Scenario = {
  slug: 'gout-hiro',
  series: 'Metabolic Health',
  title: "He did everything right. The gout kept coming back.",
  socialHook:
    "Hiro, 38: no red meat, no shellfish, almost no alcohol for a year. The attacks kept coming because the problem was not what went in. It was what his body could not get out.",
  meta: {
    description:
      "A fictional case study: Hiro followed a strict low-purine diet for a year and still had gout. His SLC2A9 and ABCG2 results explain why uric acid clearance, not diet, was the main issue.",
  },

  persona: {
    name: 'Hiro',
    age: 38,
    sex: 'Male',
    ancestry: 'Japanese',
    occupation: 'Software developer',
    chiefConcern:
      'Third gout attack in 18 months, with big toe pain so severe he cannot walk for 2-3 days. He has followed a strict low-purine diet for a year with no improvement.',
    familyHistory: 'Father: gout. Paternal grandfather: gout.',
  },

  clinicalPicture: {
    symptoms: [
      'Recurrent acute gout — third attack in 18 months',
      'Big toe: severe pain, swelling, redness lasting 2-3 days per episode',
      'No symptoms between attacks',
    ],
    labs: [
      { name: 'Serum uric acid', value: '520 µmol/L', refRange: '<360 µmol/L (men)', flag: 'high' },
      { name: 'Urine uric acid', value: 'Normal (not elevated)', refRange: 'Expected high if overproducing', flag: 'normal' },
    ],
    medications: ['Naproxen (during acute attacks only)', 'No urate-lowering therapy'],
    supplements: ['None'],
    lifestyle: [
      'Strict low-purine diet for 12 months: no red meat, no shellfish, minimal organ meats',
      'Alcohol: near-zero for 12 months (previously occasional beer)',
      'Sedentary work (desk job), moderate general activity',
      'Hydration: variable — typically 1-1.5L water daily',
    ],
  },

  genetics: [
    {
      gene: 'SLC2A9',
      variant: 'Risk allele',
      rsid: 'rs11722228',
      genotype: 'Variant genotype (reduced excretion)',
      plainMeaning:
        "Hiro's kidneys may pull too much uric acid back into the blood instead of letting it leave in urine.",
      technicalDetail:
        'SLC2A9 encodes GLUT9, the main transporter that moves uric acid from kidney tubule fluid back into the blood. This variant increases reabsorption — instead of letting uric acid leave in the urine, the kidneys pull it back into circulation. It is the single strongest genetic driver of serum uric acid levels identified to date.',
    },
    {
      gene: 'ABCG2',
      variant: 'Q141K',
      rsid: 'rs2231142',
      genotype: 'Variant (reduced function ~50%)',
      plainMeaning:
        "A second result may cut his ability to push uric acid out through the gut and kidneys, making high levels more likely even with a strict diet.",
      technicalDetail:
        'ABCG2 is a transporter in both the gut and kidneys that actively secretes uric acid for elimination. The Q141K variant reduces this function by roughly 50%. It is carried by approximately 30% of East Asian men and is a major genetic contributor to gout in this population — but is routinely missed in Western gout workups. Together with the SLC2A9 variant, Hiro has both overactive reabsorption and halved excretion capacity.',
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'A year of sacrifice, then another attack',
      body: "Hiro gave up almost everything he was told to give up. No beers with colleagues. No shellfish. No yakiniku on weekends. He became the person at dinner quietly checking every sauce. For 12 months he followed the standard low-purine diet. Then the third attack hit: same big toe, same searing pain, same 72 hours unable to walk. The day before, he had eaten plain chicken and steamed vegetables.",
    },
    {
      type: 'narrative',
      heading: 'The missing half of the gout story',
      body: "Food purines matter, but they are only part of the picture. Shellfish and red meat can raise uric acid, but most uric acid comes from the body's own cell turnover. The bigger question is whether uric acid leaves the body or stays in the blood. Hiro had focused almost entirely on intake. His labs pointed toward clearance.",
    },
    {
      type: 'reveal',
      heading: "The lab clue was in the urine",
      body: "The issue was not too much uric acid entering from food. It was too little leaving. Hiro's blood uric acid is high at 520 µmol/L, but his urine uric acid is not elevated. If he were mainly overproducing uric acid, more would be expected to spill into the urine. Instead, the pattern suggests underexcretion. His SLC2A9 result explains one way this can happen: the kidneys pull uric acid back into the blood instead of letting it leave.",
    },
    {
      type: 'reveal',
      heading: "A second clearance problem",
      body: "Hiro also has an ABCG2 Q141K result, which can reduce uric acid secretion through the gut and kidneys. This variant is common in East Asian men and can be missed when gout is treated as a diet problem first. Now the pattern is clear: one pathway pulls too much uric acid back, and another pushes too little out. His gout was not proof that he failed the diet. It was proof that diet was the wrong main lever.",
    },
    {
      type: 'action',
      heading: "Five things to discuss with his clinician",
      items: [
        "Ask specifically about urate-lowering therapy such as allopurinol or febuxostat. For recurrent gout with underexcretion, medication targets the mechanism diet cannot.",
        "Ask what uric acid target applies. Preventing new crystals and dissolving existing deposits may require different targets.",
        "Increase water intake to 2–3 liters daily if appropriate. Hydration is one lifestyle lever that can help uric acid leave through urine.",
        "If starting allopurinol, ask about HLA-B*5801 testing first. This variant is more common in East Asian men and predicts a rare but severe reaction.",
        "Relax the strict low-purine diet if it is causing stress, once treatment is in place and the clinician agrees. Diet affects only a minority of uric acid load and does not fix underexcretion.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "What is driving Hiro's uric acid",
      caption:
        "Hiro focused on the intake lever for a year. His pattern points to clearance. For SLC2A9 + ABCG2 underexcretion, medication targets the main mechanism.",
      baselineLabel: "Current serum uric acid: 520 µmol/L (target <300)",
      baseline: 520,
      levers: [
        {
          label: 'Urate-lowering therapy (allopurinol)',
          delta: -180,
          description: 'Inhibits xanthine oxidase, the enzyme that produces uric acid. The most effective single intervention — targets the core mechanism.',
        },
        {
          label: '2-3L water daily',
          delta: -30,
          description: 'Higher urine volume can increase uric acid clearance even with impaired transporters.',
        },
        {
          label: 'Reduce fructose and sugary drinks',
          delta: -20,
          description: 'Fructose raises uric acid via a separate metabolic pathway (ATP breakdown). Different mechanism from purine restriction.',
        },
        {
          label: 'Low-purine diet (already doing)',
          delta: -10,
          description: 'Reduces purine input by 10-15% at most, which shows why diet alone was unlikely to control levels with both excretion pathways impaired.',
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'gout-hiro',
    label: 'Explore your uric acid genetics in Coach',
  },
}
